import React from 'react';
import { FormProvider } from 'react-hook-form';
import { PageLayout } from '@@viewCommonComponents/forms/Layouts.jsx';
import { DashedTitle, Icon, PageTitle, RequiredFieldsNotice } from '@fasstech/middle-design-system';
import useSimulationDemographyForm from './hooks/useSimulationDemographyForm.js';
import { not, propOr } from 'ramda';
import ButtonContainer from '@@viewCommonComponents/forms/ButtonContainer.jsx';
import {
  ControlledInput,
  ControlledRadioButton,
  ControlledSelect
} from '@@viewCommonComponents/controlled/index.js';
import { isRequiredInput, isRequiredSelect } from '@@viewHelpers/validations/index.js';

const SimulationDemographyForm = ({
  getDemography,
  getMiddleText,
  getMiddleBoolean,
  getMiddleNumber,
  regimeObligatoire
}) => {
  const {
    form,
    $onPrevClick,
    isValid,
    collegeOptions,
    employeeCountTooltipText,
    spouseCountTooltipText,
    childrenCountTooltipText,
    employeeAverageAgeTooltipText,
    onSubmit
  } = useSimulationDemographyForm(getDemography, getMiddleText, getMiddleNumber);

  return (
    <FormProvider {...form}>
      <form className={'w-full'} onSubmit={form.handleSubmit(onSubmit)}>
        <PageLayout withFormLayout={false} className={'flex flex-col items-center w-full gap-6'}>
          <div className={'flex flex-col items-center gap-6'}>
            <Icon name={'circleDemography'} size={70} />
            <PageTitle>{getMiddleText('ENTREPRISE_DEMOGRAPHIE_TITLE_BLOC')}</PageTitle>
            <RequiredFieldsNotice/>
            <DashedTitle>{getMiddleText('ENTREPRISE_DEMOGRAPHIE_TITLE_SOUS_BLOC')}</DashedTitle>
            <div className={'p-4'}>
              <ControlledRadioButton
                name="demography.college"
                label={getMiddleText('ENTREPRISE_DEMOGRAPHIE_COLLEGE_LABEL')}
                options={collegeOptions}
                form={form}
                className={'whitespace-nowrap py-4 mb-4'}
                data-testid="collegeChoice"
                rules={isRequiredSelect(collegeOptions, true)}
              />
              <div className="grid grid-cols-2 gap-8">
                <ControlledInput
                  name="demography.employeeCount"
                  label={getMiddleText('ENTREPRISE_DEMOGRAPHIE_NB_SALARIES_LABEL')}
                  number={true}
                  form={form}
                  data-testid="employeeCount"
                  rules={isRequiredInput(
                    getMiddleBoolean('ENTREPRISE_DEMOGRAPHIE_NB_SALARIES_EST_OBLIGATOIRE')
                  )}
                  isDisplayed={getMiddleBoolean(
                    'ENTREPRISE_DEMOGRAPHIE_NB_SALARIES_EST_NECESSAIRE'
                  )}
                  min={getMiddleNumber('ENTREPRISE_DEMOGRAPHIE_NB_SALARIES_MIN')}
                  max={getMiddleNumber('ENTREPRISE_DEMOGRAPHIE_NB_SALARIES_MAX')}
                  tootip={employeeCountTooltipText}
                />
                <ControlledInput
                  name="demography.employeeAverageAge"
                  label={getMiddleText('ENTREPRISE_DEMOGRAPHIE_AGE_MOYEN_SALARIE_LABEL')}
                  number={true}
                  form={form}
                  data-testid="employeeAverageAge"
                  rules={isRequiredInput(true)}
                  isDisplayed={getMiddleBoolean(
                    'ENTREPRISE_DEMOGRAPHIE_AGE_MOYEN_SALARIE_EST_NECESSAIRE'
                  )}
                  min={getMiddleNumber('ENTREPRISE_DEMOGRAPHIE_AGE_MOYEN_SALARIE_MIN')}
                  max={getMiddleNumber('ENTREPRISE_DEMOGRAPHIE_AGE_MOYEN_SALARIE_MAX')}
                  tootip={employeeAverageAgeTooltipText}
                />
                <ControlledInput
                  name="demography.partnerCount"
                  label={getMiddleText('ENTREPRISE_DEMOGRAPHIE_NB_CONJOINTS_LABEL')}
                  number={true}
                  form={form}
                  data-testid="employeeSpouseCount"
                  isDisplayed={getMiddleBoolean(
                    'ENTREPRISE_DEMOGRAPHIE_NB_CONJOINTS_EST_NECESSAIRE'
                  )}
                  min={getMiddleNumber('ENTREPRISE_DEMOGRAPHIE_NB_CONJOINTS_MIN')}
                  max={getMiddleNumber('ENTREPRISE_DEMOGRAPHIE_NB_CONJOINTS_MAX')}
                  tootip={spouseCountTooltipText}
                />
                <ControlledInput
                  name="demography.childrenCount"
                  label={getMiddleText('ENTREPRISE_DEMOGRAPHIE_NB_ENFANTS_LABEL')}
                  number={true}
                  form={form}
                  data-testid="employeeChildCount"
                  isDisplayed={getMiddleBoolean('ENTREPRISE_DEMOGRAPHIE_NB_ENFANTS_EST_NECESSAIRE')}
                  min={getMiddleNumber('ENTREPRISE_DEMOGRAPHIE_NB_ENFANTS_MIN')}
                  max={getMiddleNumber('ENTREPRISE_DEMOGRAPHIE_NB_ENFANTS_MAX')}
                  tootip={childrenCountTooltipText}
                />
                <ControlledSelect
                  name="demography.socialSecurities.0.regime"
                  label={getMiddleText('ENTREPRISE_DEMOGRAPHIE_REGIME_LABEL')}
                  form={form}
                  options={propOr([], 'regimes', regimeObligatoire)}
                  data-testid="socialSecuritiesRegime"
                  rules={isRequiredSelect(propOr([], 'regimes', regimeObligatoire), true)}
                  isDisplayed={getMiddleBoolean('ENTREPRISE_DEMOGRAPHIE_REGIME_EST_NECESSAIRE')}
                />
              </div>
            </div>
          </div>
        </PageLayout>
        <ButtonContainer
          $onPreviousButtonClick={$onPrevClick}
          nextButtonLabel={getMiddleText('GENERAL_COMMON_TEXTS_NEXT_BUTTON_LABEL')}
          nextButtonIsDisabled={not(isValid)}
          isFormError={not(isValid)}
          getMiddleText={getMiddleText}
          displayResumeButton
        />
      </form>
    </FormProvider>
  );
};

export default SimulationDemographyForm;
